import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, ChevronDown, Download, Mail } from 'lucide-react';
import { Toast } from '../components/Toast';
import { ReportSettingsDrawer } from '../components/MonthlyClubReport/ReportSettingsDrawer';
import { ReportEmailPreviewModal } from '../components/MonthlyClubReport/ReportEmailPreviewModal';
import { useReportSettings } from '../contexts/ReportSettingsContext';
import type { ClubStoreMode } from '../types/monthlyClubReport';
import { NavBar } from '../components/NavBar';
import { StatCard } from '../components/StatCard';
import { Card } from '../components/SubscriptionAppCard';
import { Button } from '../components/SubscriptionAppButton';
import { DeltaLine } from '../components/MonthlyClubReport/DeltaLine';
import { ReportSection } from '../components/MonthlyClubReport/ReportSection';
import { SignupsSection } from '../components/MonthlyClubReport/SignupsSection';
import { ClubHealthSection } from '../components/MonthlyClubReport/ClubHealthSection';
import { MembersByClubSection } from '../components/MonthlyClubReport/MembersByClubSection';
import { monthlyClubReports } from '../data/mockMonthlyClubReport';
import { formatReportValue } from '../utils/reportFormat';
import type { ComparedFigure, ReportValueFormat } from '../types/monthlyClubReport';
const WINERY_NAME = 'Hillside Estate';
export function MonthlyClubReportPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const requestedMonth = searchParams.get('month');
  const [reportId, setReportId] = useState(() => monthlyClubReports.some(r => r.id === requestedMonth) ? requestedMonth as string : monthlyClubReports[0].id);
  const {
    settings
  } = useReportSettings();
  const sections = settings.sections;
  const someSectionsOff = Object.values(sections).some(on => !on);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [emailPreviewOpen, setEmailPreviewOpen] = useState(false);
  const [showSavedToast, setShowSavedToast] = useState(false);
  const [storeMode, setStoreMode] = useState<ClubStoreMode>('several');
  const report = monthlyClubReports.find(r => r.id === reportId) ?? monthlyClubReports[0];
  const {
    headline,
    revenue
  } = report;
  const statComparisons = (figure: ComparedFigure, format: ReportValueFormat, inverse = false) => <div className="space-y-1" data-id="element-11302" data-mp="1eqnr2afmyd7w">
      <DeltaLine current={figure.value} compared={figure.prev} label={report.prevShort} format={format} inverse={inverse} data-id="element-11303" data-mp="1epm6nfcqcedi" />
      <DeltaLine current={figure.value} compared={figure.yearAgo} label={report.yearAgoShort} format={format} inverse={inverse} data-id="element-11304" data-mp="19r1e823pyyzm" />
    </div>;
  const revenueRows = [{
    label: 'Sign-up revenue (first orders)',
    figure: revenue.signup,
    bold: false
  }, {
    label: 'Renewal revenue',
    figure: revenue.renewal,
    bold: false
  }, {
    label: 'Total club revenue',
    figure: {
      value: revenue.signup.value + revenue.renewal.value,
      prev: revenue.signup.prev + revenue.renewal.prev,
      yearAgo: revenue.signup.yearAgo + revenue.renewal.yearAgo
    },
    bold: true
  }];
  return <div className="bg-[#F1F5F9] min-h-full w-full p-6" data-id="element-11305" data-mp="2h25rkn5d9ofr">
      <div className="mb-6" data-id="element-11306" data-mp="233xodxhqlqw5">
        <NavBar tabs={[]} title="Reports" subtitle="Monthly Club Report" data-id="element-11307" data-mp="232mjtvggwf3z" />
      </div>

      <main className="max-w-[960px] mx-auto space-y-6" data-id="element-11308" data-mp="273g8ir1qhxxx">
        <button type="button" onClick={() => navigate('/operations/reports')} className="inline-flex items-center gap-1.5 text-sm font-medium text-[#0EA5E9] hover:text-[#0284C7] transition-colors" data-id="element-11309" data-mp="1tdmjo8q8bdo7">
          <ArrowLeft className="w-4 h-4" data-id="element-11310" data-mp="1jfgixug5sdkx" />
          Back to Reports
        </button>

        {/* 1. Report header */}
        <div data-id="element-11311" data-mp="1odi18d5rg8vc">
          <Card data-id="element-11312" data-mp="1ofb91vs40z4x">
            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4" data-id="element-11313" data-mp="1oet2o6bqh4ja">
              <div className="min-w-0" data-id="element-11314" data-mp="1af0iyuymjokt">
                <div className="text-[12px] font-semibold text-[#94A3B8] uppercase tracking-[0.5px]" data-id="element-11315" data-mp="1fdlh8oubfy6m">
                  Monthly Club Report
                </div>
                <h1 className="text-[28px] font-semibold text-[#0F172A] mt-1" data-id="element-11316" data-mp="17cds8u99rjr7">
                  {report.monthLabel}
                </h1>
                <p className="text-sm text-[#64748B] mt-1" data-id="element-11317" data-mp="xn2o7m18p5a6">
                  {WINERY_NAME} · {report.periodLabel} · Generated{' '}
                  {report.generatedAt}
                </p>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0" data-id="element-11318" data-mp="om4fwrjrqbls">
                <div className="relative" data-id="element-11319" data-mp="ok1ivnk77i7k">
                  <select value={reportId} onChange={e => setReportId(e.target.value)} aria-label="Report month" className="h-[40px] pl-3 pr-9 border border-[#CBD5E1] rounded-md bg-white text-sm text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:border-transparent appearance-none" data-id="element-11320" data-mp="jn1my5h2bvu6">
                    {monthlyClubReports.map(r => <option key={r.id} value={r.id} data-id="element-11321" data-mp="291g8kgwsb2lg">
                        {r.monthLabel}
                      </option>)}
                  </select>
                  <ChevronDown className="w-4 h-4 text-[#475569] absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" data-id="element-11322" data-mp="3mysq6tgqy7y2" />
                </div>
                <Button variant="outline" size="md" iconLeft={<Mail className="w-4 h-4" data-id="element-11324" data-mp="i6b9iszth8m" />} onClick={() => setEmailPreviewOpen(true)} data-id="element-11323" data-mp="3rvj5vfon31iy">
                  Preview email
                </Button>
                <Button variant="outline" size="md" iconLeft={<Download className="w-4 h-4" data-id="element-11326" data-mp="33zoipp4usm8i" />} onClick={() => window.print()} data-id="element-11325" data-mp="4jrrne4qkdnk">
                  Download PDF
                </Button>
              </div>
            </div>
            {someSectionsOff && <p className="text-xs text-[#94A3B8] mt-4" data-id="element-11327" data-mp="3d0cwciqc4kkv">
                Some sections are turned off in{' '}
                <button type="button" onClick={() => setSettingsOpen(true)} className="font-medium text-[#0EA5E9] hover:text-[#0284C7] hover:underline" data-id="element-11328" data-mp="3hzpif19dtnme">
                  Report settings
                </button>
              </p>}
          </Card>
          <p className="text-sm text-[#64748B] mt-3" data-id="element-11329" data-mp="x7e0p1wqgaht">
            Changes are compared with {report.prevMonthLabel} and{' '}
            {report.yearAgoLabel}.
          </p>
        </div>

        {/* 2. Headline numbers */}
        <section aria-label="Headline numbers" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-11330" data-mp="2pcuomq433poc">
          <StatCard label="Active members at end of month" value={formatReportValue(headline.members.value, 'number')} trend={statComparisons(headline.members, 'number')} data-id="element-11331" data-mp="2yb6m9c3dbnqa" />
          <StatCard label="New sign-ups" value={formatReportValue(headline.signups.value, 'number')} trend={statComparisons(headline.signups, 'number')} data-id="element-11332" data-mp="2ajj5vo3qfv29" />
          <StatCard label="Cancellations" value={formatReportValue(headline.cancellations.value, 'number')} trend={statComparisons(headline.cancellations, 'number', true)} data-id="element-11333" data-mp="2kboyn01t6gii" />
          <StatCard label="Club revenue" value={formatReportValue(headline.revenue.value, 'currency')} trend={statComparisons(headline.revenue, 'currency')} data-id="element-11334" data-mp="1ruunb8sq21tn" />
        </section>

        {/* 3. Sign-ups */}
        {sections.signups && <SignupsSection report={report} data-id="element-11335" data-mp="2cxs3a34x1kq" />}

        {/* 4. Revenue */}
        {sections.revenue && <ReportSection id="revenue" title="Revenue" data-id="element-11336" data-mp="74yzoipcy44f">
          <div className="overflow-x-auto" data-id="element-11337" data-mp="76s5jvs5ckbd">
            <table className="w-full text-sm" data-id="element-11338" data-mp="3pjhpw31fltqn">
              <thead data-id="element-11339" data-mp="3pglr6chrf115">
                <tr className="border-b border-[#E2E8F0] text-[12px] font-semibold text-[#94A3B8] uppercase tracking-[0.5px]" data-id="element-11340" data-mp="3tka0azm1i4oi">
                  <th scope="col" className="text-left py-2 pr-4" data-id="element-11341" data-mp="l48h1lpav49z">
                    <span className="sr-only" data-id="element-11342" data-mp="q3kzzvd1vr7a">Revenue type</span>
                  </th>
                  <th scope="col" className="text-right py-2 px-4" data-id="element-11343" data-mp="q17a00x8956l">This month</th>
                  <th scope="col" className="text-right py-2 px-4" data-id="element-11344" data-mp="c3ipwvazi22l">{report.prevColumn}</th>
                  <th scope="col" className="text-right py-2 pl-4" data-id="element-11345" data-mp="h1bvbm4gqs1t">{report.yearAgoShort}</th>
                </tr>
              </thead>
              <tbody data-id="element-11346" data-mp="31t44uwh9kv5k">
                {revenueRows.map(row => <tr key={row.label} className={row.bold ? 'font-semibold text-[#0F172A]' : 'border-b border-[#E2E8F0]'} data-id="element-11347" data-mp="31tvqpfl27ein">
                    <td className="py-3 pr-4 text-[#0F172A]" data-id="element-11348" data-mp="3skikiucwka0k">{row.label}</td>
                    <td className="py-3 px-4 text-right text-[#0F172A]" data-id="element-11349" data-mp="3ogsz2driy8jr">
                      {formatReportValue(row.figure.value, 'currency')}
                    </td>
                    <td className={`py-3 px-4 text-right ${row.bold ? '' : 'text-[#475569]'}`} data-id="element-11350" data-mp="3ejxyzqfls03d">
                      {formatReportValue(row.figure.prev, 'currency')}
                    </td>
                    <td className={`py-3 pl-4 text-right ${row.bold ? '' : 'text-[#475569]'}`} data-id="element-11351" data-mp="b2xv71sqiblf">
                      {formatReportValue(row.figure.yearAgo, 'currency')}
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>
        </ReportSection>}

        {/* 5. Forecast */}
        {sections.forecast && <ReportSection id="forecast" title={`Next 12 months (forecast at ${report.forecast.asAt})`} data-id="element-11352" data-mp="ytuyxradm2ll">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-11353" data-mp="tzz2it9rgnsj">
            <div data-id="element-11354" data-mp="11dhy7jycp162">
              <div className="text-[12px] font-semibold text-[#94A3B8] uppercase tracking-[0.5px]" data-id="element-11355" data-mp="11dzyqss1q1js">
                Forecast revenue
              </div>
              <div className="text-[28px] font-semibold text-[#0F172A] mt-1" data-id="element-11356" data-mp="1bb5re5gb60m4">
                {formatReportValue(report.forecast.revenue, 'currency')}
              </div>
            </div>
            <div data-id="element-11357" data-mp="1kbkf1bgqvrss">
              <div className="text-[12px] font-semibold text-[#94A3B8] uppercase tracking-[0.5px]" data-id="element-11358" data-mp="1kc3y4vi4sm8e">
                Forecast orders
              </div>
              <div className="text-[28px] font-semibold text-[#0F172A] mt-1" data-id="element-11359" data-mp="1p8ue7itxrhtt">
                {formatReportValue(report.forecast.orders, 'number')}
              </div>
            </div>
          </div>
          <p className="text-xs text-[#94A3B8] mt-4" data-id="element-11360" data-mp="1yzoyihnmg7cm">
            Based on active subscriptions, their frequency and current pack
            prices. Paused subscriptions are not included.
          </p>
        </ReportSection>}

        {/* 6. Club health */}
        {sections.health && <ClubHealthSection report={report} showClubsLink={sections.clubs} storeMode={storeMode} data-id="element-11361" data-mp="2ek24dtar6xq1" />}

        {/* 7. Members by club */}
        {sections.clubs && <div className="space-y-3" data-id="element-11362" data-mp="2ogx0k5jk0m15">
            {/* Prototype-only preview switch */}
            <div className="print:hidden flex flex-wrap items-center gap-3 px-3 py-2 border border-dashed border-[#CBD5E1] rounded-lg bg-white/60" data-id="element-11363" data-mp="2of3xf085gjxw">
              <span className="text-[11px] font-semibold text-[#94A3B8] uppercase tracking-wider" data-id="element-11364" data-mp="2y5otoh0cpcdn">
                Demo
              </span>
              <span className="text-[13px] text-[#475569]" data-id="element-11365" data-mp="32a69k53lb5j5">Preview:</span>
              <div className="inline-flex p-0.5 bg-[#F1F5F9] rounded-md" role="group" aria-label="Store club setup preview" data-id="element-11366" data-mp="377xz4sgrifk6">
                {([['several', 'Several clubs'], ['one', 'One club']] as const).map(([mode, label]) => <button key={mode} type="button" onClick={() => setStoreMode(mode)} aria-pressed={storeMode === mode} className={`px-3 h-7 rounded text-[13px] font-medium transition-colors ${storeMode === mode ? 'bg-white text-[#0EA5E9] shadow-sm' : 'text-[#64748B] hover:text-[#0F172A]'}`} data-id="element-11367" data-mp="1cvhx63v01nl4">
                    {label}
                  </button>)}
              </div>
              {storeMode === 'one' && <span className="text-[12px] text-[#94A3B8]" data-id="element-11368" data-mp="10jfjtoyumkl1">Hillside Club</span>}
            </div>
            <MembersByClubSection report={report} storeMode={storeMode} data-id="element-11369" data-mp="vlw56ny8o5sk" />
          </div>}

        {/* 8. Footer note */}
        <p className="text-xs text-[#94A3B8] pb-4" data-id="element-11370" data-mp="1t5h3e1je1ooa">
          Figures cover club subscriptions only. Members means active
          subscriptions at the end of the month; paused subscriptions are
          counted as members, cancelled are not.
        </p>
      </main>

      <ReportSettingsDrawer isOpen={settingsOpen} onClose={() => setSettingsOpen(false)} onSaved={() => setShowSavedToast(true)} data-id="element-11371" data-mp="1ea13hfw4myjz" />
      <ReportEmailPreviewModal isOpen={emailPreviewOpen} onClose={() => setEmailPreviewOpen(false)} report={report} wineryName={WINERY_NAME} data-id="element-11372" data-mp="7s497krcdpof" />
      {showSavedToast && <Toast id="report-settings-saved" type="success" title="Report settings saved" onClose={() => setShowSavedToast(false)} data-id="element-11373" data-mp="2r2no6k6csosv" />}
    </div>;
}